import { useState } from 'react';
import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { UploadModal } from '@/components/UploadModal';
import { GoLiveModal } from '@/components/GoLiveModal';
import { VideoCard, VideoCardSkeleton } from '@/components/VideoCard';
import { ShortCard, ShortCardSkeleton } from '@/components/ShortCard';
import { ShortsViewer } from '@/components/ShortsViewer';
import { LiveStreamCard, LiveStreamCardSkeleton } from '@/components/LiveStreamCard';
import { useFilteredVideoFeed, useFilteredShortsFeed } from '@/hooks/useFilteredVideoFeed';
import { useLiveStreams } from '@/hooks/useLiveStreams';
import { useIsMobile } from '@/hooks/useIsMobile';

const CATEGORY_FILTERS = [
  { label: 'All',        tag: undefined },
  { label: 'Bitcoin',    tag: 'bitcoin' },
  { label: 'Nostr',      tag: 'nostr' },
  { label: 'Technology', tag: 'tech' },
  { label: 'Gaming',     tag: 'gaming' },
  { label: 'Music',      tag: 'music' },
  { label: 'Podcast',    tag: 'podcast' },
  { label: 'Tutorial',   tag: 'tutorial' },
  { label: 'NSFW',       tag: 'nsfw' },
];

export default function Index() {
  useSeoMeta({
    title: 'Goblinbox – Decentralized Video Platform',
    description: 'A censorship-resistant, decentralized video platform powered by the Nostr protocol.',
  });

  const [uploadOpen, setUploadOpen] = useState(false);
  const [goLiveOpen, setGoLiveOpen] = useState(false);
  const [activeTag, setActiveTag] = useState<string | undefined>(undefined);
  const [shortsViewerIndex, setShortsViewerIndex] = useState<number | null>(null);
  const isMobile = useIsMobile();

  const { data: allVideos, isLoading } = useFilteredVideoFeed();
  const { data: filteredVideos, isLoading: filterLoading } = useFilteredVideoFeed(activeTag);
  const { data: shortsVideos, isLoading: shortsLoading } = useFilteredShortsFeed();
  const { data: liveStreams, isLoading: liveLoading } = useLiveStreams();

  // Trending = top 12 newest from all
  const trendingVideos = allVideos ? [...allVideos].sort((a, b) => b.created_at - a.created_at).slice(0, 12) : undefined;
  const displayVideos = activeTag ? filteredVideos : allVideos;
  const displayLoading = activeTag ? filterLoading : isLoading;

  return (
    <Layout onUploadClick={() => setUploadOpen(true)} onGoLiveClick={() => setGoLiveOpen(true)}>
      <div className="px-4 sm:px-6 py-4" style={{ maxWidth: 1400, margin: '0 auto' }}>

        {/* ── Live Streams section ─────────────────────────────────── */}
        {!activeTag && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                {/* Pulsing red dot */}
                <span className="relative flex items-center justify-center w-5 h-5">
                  <span className="absolute inline-block w-3 h-3 rounded-full opacity-60" style={{ background: '#f44336', animation: 'ping 1.5s cubic-bezier(0,0,0.2,1) infinite' }} />
                  <span className="relative inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#f44336' }} />
                </span>
                Live Now
              </h2>
              <Link to="/live" className="text-sm no-underline hover:underline" style={{ color: 'var(--gb-accent)' }}>
                View all
              </Link>
            </div>

            {liveLoading ? (
              <div className="gb-trending-scroll">
                {Array.from({ length: 4 }).map((_, i) => <LiveStreamCardSkeleton key={i} />)}
              </div>
            ) : liveStreams && liveStreams.length > 0 ? (
              <div className="gb-trending-scroll">
                {liveStreams.map(event => <LiveStreamCard key={event.id} event={event} />)}
              </div>
            ) : (
              <div className="rounded-xl py-8 px-6 text-center" style={{ border: '2px dashed var(--gb-border)' }}>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  No one is live right now.{' '}
                  <button
                    onClick={() => setGoLiveOpen(true)}
                    className="hover:underline font-semibold"
                    style={{ color: '#f44336' }}
                  >
                    Be the first to go live!
                  </button>
                </p>
              </div>
            )}
          </section>
        )}

        {/* ── Trending section ────────────────────────────────────── */}
        {!activeTag && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--gb-accent)' }}>
                  <path d="M16 6l2.29 2.29-4.88 4.88-4-4L2 16.59 3.41 18l6-6 4 4 6.3-6.29L22 12V6z" />
                </svg>
                Trending
              </h2>
              <Link
                to="/trending"
                className="text-sm no-underline hover:underline"
                style={{ color: 'var(--gb-accent)' }}
              >
                View all
              </Link>
            </div>

            {isLoading ? (
              <div className="gb-trending-scroll">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div key={i} style={{ width: 250, flexShrink: 0 }}>
                    <VideoCardSkeleton />
                  </div>
                ))}
              </div>
            ) : trendingVideos && trendingVideos.length > 0 ? (
              <div className="gb-trending-scroll">
                {trendingVideos.map(event => (
                  <VideoCard key={event.id} event={event} compact />
                ))}
              </div>
            ) : (
              <EmptyState message="No trending videos yet — check back soon!" />
            )}
          </section>
        )}

        {/* ── Shorts section ──────────────────────────────────────── */}
        {!activeTag && (
          <section className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
                {/* Shorts / Reels icon */}
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--gb-accent)' }}>
                  <path d="M17 10.5V7c0-.55-.45-1-1-1H4c-.55 0-1 .45-1 1v10c0 .55.45 1 1 1h12c.55 0 1-.45 1-1v-3.5l4 4v-11l-4 4z" />
                </svg>
                Shorts
              </h2>
              <Link
                to="/tag/shorts"
                className="text-sm no-underline hover:underline"
                style={{ color: 'var(--gb-accent)' }}
              >
                View all
              </Link>
            </div>

            {shortsLoading ? (
              <div className="gb-trending-scroll" style={{ alignItems: 'flex-start' }}>
                {Array.from({ length: 7 }).map((_, i) => (
                  <ShortCardSkeleton key={i} />
                ))}
              </div>
            ) : shortsVideos && shortsVideos.length > 0 ? (
              <div className="gb-trending-scroll" style={{ alignItems: 'flex-start' }}>
                {shortsVideos.map((event, idx) => (
                  <ShortCard
                    key={event.id}
                    event={event}
                    onMobileClick={isMobile ? () => setShortsViewerIndex(idx) : undefined}
                  />
                ))}
              </div>
            ) : (
              <div
                className="rounded-xl py-10 px-6 text-center"
                style={{ border: '2px dashed var(--gb-border)' }}
              >
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  No shorts yet — upload a short-form video with <span style={{ color: 'var(--gb-accent)' }}>#shorts</span> to appear here.
                </p>
              </div>
            )}
          </section>
        )}

        {/* ── Divider ─────────────────────────────────────────────── */}
        {!activeTag && <hr style={{ border: 'none', borderTop: '1px solid var(--gb-border)', marginBottom: '1.5rem' }} />}

        {/* ── All Videos header ───────────────────────────────────── */}
        <div className="flex items-center justify-between mb-3">
          <h2 className="flex items-center gap-2 text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" style={{ color: 'var(--gb-accent)' }}>
              <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-8 12.5v-9l6 4.5-6 4.5z" />
            </svg>
            {activeTag ? `#${activeTag}` : 'All Videos'}
          </h2>
        </div>

        {/* ── Category chip filters ───────────────────────────────── */}
        <div
          className="flex gap-2 overflow-x-auto pb-3 mb-5 scrollbar-hide"
          style={{ marginLeft: -4, paddingLeft: 4 }}
        >
          {CATEGORY_FILTERS.map(cat => (
            <button
              key={cat.label}
              onClick={() => setActiveTag(cat.tag)}
              className="gb-chip"
              style={activeTag === cat.tag
                ? { background: 'var(--gb-accent)', color: '#fff', borderColor: 'var(--gb-accent)' }
                : undefined}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* ── Video grid ──────────────────────────────────────────── */}
        {displayLoading ? (
          <div className="video-grid">
            {Array.from({ length: 8 }).map((_, i) => <VideoCardSkeleton key={i} />)}
          </div>
        ) : displayVideos && displayVideos.length > 0 ? (
          <div className="video-grid">
            {displayVideos.map(event => (
              <VideoCard key={event.id} event={event} />
            ))}
          </div>
        ) : (
          <EmptyState
            message={activeTag
              ? `No videos found for "#${activeTag}". Try a different category.`
              : 'No videos found. Connect to more relays or check back later.'}
            action={activeTag ? (
              <button onClick={() => setActiveTag(undefined)} className="text-sm hover:underline mt-3" style={{ color: 'var(--gb-accent)' }}>
                View all videos
              </button>
            ) : undefined}
          />
        )}
      </div>

      <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <GoLiveModal open={goLiveOpen} onClose={() => setGoLiveOpen(false)} />

      {/* Mobile fullscreen shorts viewer */}
      {isMobile && shortsViewerIndex !== null && shortsVideos && shortsVideos.length > 0 && (
        <ShortsViewer
          events={shortsVideos}
          initialIndex={shortsViewerIndex}
          onClose={() => setShortsViewerIndex(null)}
        />
      )}
    </Layout>
  );
}

function EmptyState({ message, action }: { message: string; action?: React.ReactNode }) {
  return (
    <div
      className="rounded-xl py-16 px-8 text-center"
      style={{ border: '2px dashed var(--gb-border)' }}
    >
      <div className="text-5xl mb-4">📹</div>
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{message}</p>
      {action}
    </div>
  );
}
